// Sign in with Apple on the website, in Apple's popup (appleid.auth.js), as
// the Services ID grouped with the iOS app's App ID: an Apple Account has the
// same user ID (`sub`) in both, so it signs in to the same Readlang account.
// Kept free of other modules for the landing page's bundle. See docs/ios/app.md,
// "Sign in with Apple on the website".

const SERVICES_ID = "com.readlang.web";
const SCRIPT_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

interface AppleIDSignInResponse {
  authorization: { code: string; id_token: string; state: string };
  // Only on the user's first ever Sign in with Apple
  user?: { name?: { firstName?: string; lastName?: string } };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init(config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          nonce: string;
          usePopup: boolean;
        }): void;
        signIn(): Promise<AppleIDSignInResponse>;
      };
    };
  }
}

/** As the iOS app's bridge gives it, with the nonce the website asked with */
export interface AppleAuthorization {
  identityToken: string;
  authorizationCode: string;
  user: { givenName?: string; familyName?: string } | null;
  nonce: string;
}

let scriptLoading: Promise<unknown> | undefined;
// The sign-in Apple's script was last set up for
let prepared: { nonce: string; state: string } | undefined;

const sha256 = async (text: string) =>
  Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text))
    ),
    (byte) => byte.toString(16).padStart(2, "0")
  ).join("");

const loadScript = () =>
  window.AppleID
    ? Promise.resolve()
    : (scriptLoading ??= new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = SCRIPT_URL;
        script.onload = resolve;
        script.onerror = () => {
          // So that a later prepare() loads it again
          scriptLoading = undefined;
          script.remove();
          reject(new Error(`Couldn't load ${SCRIPT_URL}`));
        };
        document.head.append(script);
      }));

/**
 * Loads Apple's script and sets up a sign-in, so a click can open the popup
 * at once: browsers block popups opened after waiting on anything. Resolves
 * with whether it's ready, so the button only shows once a click can work.
 */
export const prepare = async () => {
  try {
    await loadScript();
    const nonce = crypto.randomUUID();
    const state = crypto.randomUUID();
    // Apple signs the nonce's hash into the identity token, and the server
    // checks it against the nonce (appleAuthRouter.js)
    const hashedNonce = await sha256(nonce);
    window.AppleID?.auth.init({
      clientId: SERVICES_ID,
      scope: "name email",
      // Never loaded: the popup posts its answer back to this page
      redirectURI: `${window.location.origin}/`,
      state,
      nonce: hashedNonce,
      usePopup: true,
    });
    prepared = { nonce, state };
    return true;
  } catch (error) {
    console.error("Sign in with Apple unavailable", error);
    return false;
  }
};

/**
 * Opens Apple's popup, resolving with its authorization for
 * /api/auth/appleSignIn. Call straight from a click, after prepare(). Rejects
 * with `cancelled` set if the user closes the popup.
 */
export const authorize = async (): Promise<AppleAuthorization> => {
  const attempt = prepared;
  if (!attempt || !window.AppleID) {
    throw new Error("Sign in with Apple isn't ready");
  }
  // Each sign-in has its own nonce and state
  prepared = undefined;
  let response: AppleIDSignInResponse;
  try {
    response = await window.AppleID.auth.signIn();
  } catch (error) {
    throw Object.assign(new Error(`Sign in with Apple: ${error?.error}`), {
      cancelled: ["popup_closed_by_user", "user_cancelled_authorize"].includes(
        error?.error
      ),
    });
  } finally {
    void prepare();
  }
  if (response.authorization.state !== attempt.state) {
    throw new Error("Sign in with Apple: state doesn't match");
  }
  const name = response.user?.name;
  return {
    identityToken: response.authorization.id_token,
    authorizationCode: response.authorization.code,
    user: name
      ? { givenName: name.firstName, familyName: name.lastName }
      : null,
    nonce: attempt.nonce,
  };
};
